import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { fmtINR, monthName } from '@/lib/format';
import { Button } from '@/components/ui/button';
import { Printer, Download } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

export default function BankReportPrintPage() {
  const { runId } = useParams();
  const [run, setRun] = useState(null);
  const [company, setCompany] = useState(null);

  useEffect(() => {
    (async () => {
      const r = await api.get(`/payroll/runs/${runId}`); setRun(r.data);
      const c = await api.get(`/companies/${r.data.company_id}`); setCompany(c.data);
    })();
  }, [runId]);

  if (!run) return <div className="p-8 text-muted-foreground">Loading…</div>;

  const csv = () => {
    const header = ['Employee Name','Employee Code','Bank Account','IFSC','Net Amount (INR)'];
    const rows = run.entries.map((e) => [e.name, e.employee_code || '', e.bank_account || '', e.bank_ifsc || '', e.net_pay]);
    const content = [header, ...rows].map((r) => r.map((c) => `"${(c ?? '').toString().replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([content], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `bank-report-${company?.name}-${monthName(run.month)}-${run.year}.csv`; a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-white p-6 md:p-12 print-page">
      <div className="no-print mb-4 flex justify-between items-center">
        <a href={-1} onClick={(e) => { e.preventDefault(); window.close(); }} className="text-sm text-primary hover:underline">← Close</a>
        <div className="flex gap-2">
          <Button variant="outline" onClick={csv} data-testid="download-bank-csv"><Download className="h-4 w-4 mr-2" />CSV</Button>
          <Button onClick={() => window.print()} data-testid="print-bank-report"><Printer className="h-4 w-4 mr-2" />Print / PDF</Button>
        </div>
      </div>
      <div className="max-w-5xl mx-auto">
        <div className="flex items-start justify-between border-b pb-4">
          <div>
            <h1 className="font-display text-2xl font-semibold">Bank Payment Advice</h1>
            <p className="text-sm text-muted-foreground">Payroll for {monthName(run.month)} {run.year} · {run.entries.length} employees</p>
          </div>
          <div className="text-right">
            <p className="font-display font-semibold">{company?.name}</p>
            <p className="text-sm text-muted-foreground">Total: <span className="font-medium">{fmtINR(run.totals.net_pay)}</span></p>
          </div>
        </div>
        <div className="mt-6 rounded-lg border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>#</TableHead>
                <TableHead>Employee</TableHead>
                <TableHead>Employee code</TableHead>
                <TableHead>Bank account</TableHead>
                <TableHead>IFSC</TableHead>
                <TableHead className="text-right">Amount</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {run.entries.map((e, i) => (
                <TableRow key={e.employee_id}>
                  <TableCell>{i + 1}</TableCell>
                  <TableCell className="font-medium">{e.name}</TableCell>
                  <TableCell>{e.employee_code || '—'}</TableCell>
                  <TableCell className="font-mono text-xs">{e.bank_account || '—'}</TableCell>
                  <TableCell className="font-mono text-xs">{e.bank_ifsc || '—'}</TableCell>
                  <TableCell className="text-right tabular-nums font-medium">{fmtINR(e.net_pay)}</TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell colSpan={5} className="font-semibold text-right">Total</TableCell>
                <TableCell className="text-right tabular-nums font-semibold">{fmtINR(run.totals.net_pay)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
        <div className="grid grid-cols-4 gap-4 mt-6 text-sm">
          <div className="rounded-lg border p-3"><p className="text-xs text-muted-foreground uppercase">PF (employee)</p><p className="tabular-nums font-medium">{fmtINR(run.totals.pf_employee)}</p></div>
          <div className="rounded-lg border p-3"><p className="text-xs text-muted-foreground uppercase">ESI (employee)</p><p className="tabular-nums font-medium">{fmtINR(run.totals.esi_employee)}</p></div>
          <div className="rounded-lg border p-3"><p className="text-xs text-muted-foreground uppercase">TDS</p><p className="tabular-nums font-medium">{fmtINR(run.totals.tds)}</p></div>
          <div className="rounded-lg border p-3"><p className="text-xs text-muted-foreground uppercase">Prof. tax</p><p className="tabular-nums font-medium">{fmtINR(run.totals.professional_tax)}</p></div>
        </div>
        <p className="text-[10px] text-muted-foreground mt-6">Please initiate the bank transfer above. Payment advice generated on {new Date().toLocaleString()}.</p>
      </div>
    </div>
  );
}
